import React, { useEffect, useRef, useMemo } from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import useDragItem from '../../../hooks/use-dnd/use-drag-item';
import { DRAG_TYPE } from '../../../stores/reducers/user-interface';
import { FEED_TYPES } from '../../../stores/reducers/shots';
import { GreenroomState as GS } from '../../../stores/types/types';
import { ThumbnailLocation } from '../../../types/enums';
import useShots from '../../../contexts/shots/provider';
import DefaultSafetyImage from '../../../images/default-safety-image.png';
import { useLivekit } from '../../../contexts/livekit/livekit-context';

interface Props {
	ingressStreamId: string;
	participantIdentity?: string;
	location: ThumbnailLocation;
}

export default function IngressStreamThumbnail({ ingressStreamId, participantIdentity, location }: Props) {
	const { FeedsTab, ShotModal } = ThumbnailLocation;

	const host = useSelector((s: GS) => s.SessionReducer.host);

	const {
		shotModalShot: { shot_data: { shotFeeds: modalShotFeeds } },
	} = useSelector((s: GS) => s.ShotsReducer);

	const { currentShot: { shot_data: { shotFeeds: currentShotFeeds } } } = useShots();

	const feeds = useMemo(
		() => (location === ThumbnailLocation.ShotModal ? modalShotFeeds : currentShotFeeds),
		[location, modalShotFeeds, currentShotFeeds],
	);

	const inShot = useMemo(
		() => feeds.some((f) => f.feedType === FEED_TYPES.INGRESS_STREAM && f.ingressStreamId === ingressStreamId),
		[feeds, ingressStreamId],
	);

	const canDrag = useMemo(
		() => host && (location === FeedsTab || location === ShotModal),
		[host, location],
	);

	const { dragRef, isDragging } = useDragItem({ id: ingressStreamId, type: DRAG_TYPE.FEED_INGRESS_STREAM }, canDrag);

	const { tracksMap } = useLivekit();
	const videoTrack = participantIdentity ? tracksMap.get(participantIdentity) : undefined;
	const videoRef = useRef<HTMLVideoElement>(null);

	useEffect(() => {
		if (!videoRef.current || !videoTrack) return;
		videoTrack.attach(videoRef.current);
		return () => {
			videoTrack.detach(videoRef.current!);
		};
	}, [videoTrack]);

	return (
		<>
			{isDragging && <div className="video-thumb-drag-placeholder" />}
			<div
				ref={dragRef}
				className={classNames('video-thumb', {
					'is-dragging': isDragging,
					'cursor-grab': canDrag,
				})}
			>
				<div className="video-wrapper">
					<div className="video-overlay flex-center">
						{videoTrack ? (
							<video
								ref={videoRef}
								autoPlay
								playsInline
								muted
								className="width-full height-full"
								style={{ objectFit: 'cover' }}
							/>
						) : (
							<div className="poster">
								<img
									className="poster-image"
									src={DefaultSafetyImage}
									alt="LiveKit Stream"
									onError={({ currentTarget }) => {
										currentTarget.src = DefaultSafetyImage;
									}}
								/>
							</div>
						)}
						<div className="video-overlay-bottom">
							<label className="bl-font-l1">{'LiveKit Stream'}</label>
						</div>
					</div>
					<div className={classNames('selection-border', inShot && 'in-shot')} />
				</div>
			</div>
		</>
	);
}